import React, { useCallback, useEffect, useState } from "react";
import Modal from "./Modal";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { X, ChevronLeft, Download } from "lucide-react";
import { useSelector } from "react-redux";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import loader from "../../../../components/recharts/loader.gif";
import * as XLSX from "xlsx";
import {
  fetchLgbrTableData,
  fetchLgbrGetData,
} from "../../../../Rest_api/DemoRestApi";
import { downloadFile } from "../../../../components/DownloadFile";
import { BASE_URL } from "../../../../configs";
import { getAccessToken } from "../../../Protected";

const HeaderCell: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <th className="py-3 text-[14px] px-4 text-center font-medium text-[#F1935C] whitespace-nowrap last:border-r-0 border-r !border-[#F1935C]">
    {title}
  </th>
);

interface LgbrData {
  demand_mw: number;
  surplus_deficit: number;
  time_slot: number;
  total_available: number;
  total_generation: number;
  total_must_run: number;
}

type DrillDownType =
  | "total_generation"
  | "total_must_run"
  | "total_available"
  | null;

export default function LgbrTable() {
  const preSelectedDate = useSelector(
    (state: any) => state.modDemoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession
  );
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [data, setData] = useState<LgbrData[]>([]);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New state for drill-down view
  const [drillDownView, setDrillDownView] = useState<DrillDownType>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<number | null>(null);
  const [detailedData, setDetailedData] = useState<any[]>([]);
  const [detailedLoading, setDetailedLoading] = useState(false);

  const handleFetchData = useCallback(async () => {
    if (!preSelectedDate || !modSelectedSession) return;

    try {
      setLoading(true);
      const res = await fetchLgbrTableData(preSelectedDate, modSelectedSession);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [modSelectedSession, preSelectedDate]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData]);

  useEffect(() => {
    let filtered: LgbrData[] = data;
    if (selectedBlock.length > 0) {
      const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      filtered = filtered.filter((row) =>
        blocks.includes(Number(row.time_slot))
      );
    }
    setFilterData(filtered);
  }, [data, selectedBlock]);

  // Fetch detailed data when drill-down view changes
  useEffect(() => {
    const fetchDetailedData = async () => {
      if (
        !drillDownView ||
        selectedTimeSlot === null ||
        !preSelectedDate ||
        !modSelectedSession
      ) {
        setDetailedData([]);
        return;
      }

      try {
        setDetailedLoading(true);
        const res = await fetchLgbrGetData(
          drillDownView,
          selectedTimeSlot,
          preSelectedDate,
          modSelectedSession
        );
        setDetailedData(res.data || res || []);
      } catch (error) {
        console.error("Error fetching detailed data:", error);
        setError("An error occurred while fetching detailed data.");
        setDetailedData([]);
      } finally {
        setDetailedLoading(false);
      }
    };

    fetchDetailedData();
  }, [drillDownView, selectedTimeSlot, preSelectedDate, modSelectedSession]);

  const handleColumnClick = (type: DrillDownType, timeSlot: number) => {
    setDrillDownView(type);
    setSelectedTimeSlot(timeSlot);
  };

  const handleBackToMain = () => {
    setDrillDownView(null);
    setSelectedTimeSlot(null);
    setDetailedData([]);
  };

  const downloadTable = async () => {
           setError(null);
           await downloadFile({
             url: `${BASE_URL}/mod_demo/get-lgbr-download?date=${preSelectedDate}&session=${modSelectedSession}`,
             filename: `Load Generation Balance Report_${preSelectedDate}.xlsx`,
             headers: {
               Authorization: `${getAccessToken() || ""}`,
             },
             onSuccess: () => setLoading(false),
             onError: (err) => {
               setError("Failed to download Excel file. Please try again later.");
             },
           });
         };
  

  const getColumnTitle = () => {
    switch (drillDownView) {
      case "total_generation":
        return "Total Capacity (MW)";
      case "total_must_run":
        return "Must Run (MW)";
      case "total_available":
        return "Surrendable Power (MW)";
      default:
        return "";
    }
  };

  // Render drill-down table
  const renderDrillDownTable = () => {
    return (
      <div className="">
        {detailedLoading ? (
          <div className="h-full w-full bg-white flex items-center justify-center">
            <img
              src={loader}
              width={200}
              alt="loader"
              className="block mt-[5rem]"
            />
          </div>
        ) : (
          <table id="my-table_load">
            <thead className="sticky top-0 z-10">
              <tr className="text-sm">
                <HeaderCell title="Generator Name" />
                <HeaderCell title={getColumnTitle()} />
              </tr>
            </thead>
            <tbody className="text-[#8C8A96] font-normal text-[13px]">
              {detailedData.length > 0 ? (
                detailedData.map((item: any, idx: number) => (
                  <tr
                    key={idx}
                    className="border-b border-gray-200 hover:bg-gray-100 text-center"
                  >
                    <td className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-white !border-r !border-[#F1935C]">
                      {item.generator || "-"}
                    </td>
                    <td className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-blue-50">
                      {item.value}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className="py-8 text-center text-[#8C8A96]">
                    No data available for this selection
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className="flex items-center gap-1 p-2 text-sm rounded-2xl border border-[#34656D] bg-[#EFFCFF] text-[#34656D] font-medium"
      >
        LGBR
      </button>
      <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
        <div className="w-full">
          <div className="h-[calc(-73px+100vh)]">
            <div className="w-full h-full flex flex-col gap-4">
              <div
                className="gas-chart-container"
                style={{
                  height: "calc(-60px + 100vh)",
                  borderRadius: "0px!important",
                }}
              >
                {drillDownView ? (
                  <>
                    <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] pb-2">
                      <button
                        onClick={handleBackToMain}
                        className="flex justify-around text-sm text-[#34656D] font-medium items-center"
                      >
                        <ChevronLeft size={24} />
                        Back
                      </button>

                      <h2 className="chartHeading !text-lg !font-medium ">
                        Time Block: {selectedTimeSlot}
                      </h2>
                      <X
                        size={25}
                        className="text-[#A8A29F] cursor-pointer hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          setModalOpen(false);
                          setDrillDownView(null);
                          setSelectedTimeSlot(null);
                          setDetailedData([]);
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] pb-2">
                      {!drillDownView && (
                        <BlockRangeSelector
                          blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                          placeholder="Select Block"
                          selectedOptions={selectedBlock.map(
                            (item) => item.value
                          )}
                          setSelectedOptions={setSelectedBlock}
                        />
                      )}

                      <h2 className="chartHeading !text-lg !font-medium ">
                        Load Generation Balance Report
                      </h2>
                      <div className="flex items-center gap-4">
                          <button
                            onClick={downloadTable}
                            className="flex items-center gap-2 p-2 text-sm rounded-xl bg-[#FFFAF6] text-[#F1935C] font-medium border border-[#F1935C]"
                          >
                            <span>Download</span>
                            <Download className="w-5 h-5" />
                          </button>

                          <X
                            size={25}
                            className="text-[#A8A29F] cursor-pointer hover:text-gray-600 transition-colors"
                            onClick={(e) => {
                              setModalOpen(false);
                              setDrillDownView(null);
                              setSelectedTimeSlot(null);
                              setDetailedData([]);
                            }}
                          />
                        </div>
                    </div>
                  </>
                )}

                <div className="overflow-auto capitalize text-nowrap">
                  {drillDownView ? (
                    renderDrillDownTable()
                  ) : (
                    <table id="my-table_Lgbr">
                      <thead className="sticky top-0 z-10">
                        <tr className="text-sm">
                          <HeaderCell title="Time Block" />
                          <HeaderCell title="Total Capacity (MW)" />
                          <HeaderCell title="Must Run (MW)" />
                          <HeaderCell title="Surrendable Power (MW)" />
                          <HeaderCell title="Demand (MW)" />
                          <HeaderCell title="Surplus/Deficit (MW)" />
                        </tr>
                      </thead>
                      <tbody className="text-[#8C8A96] font-normal text-[13px]">
                        {loading ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="py-8 text-center text-[#8C8A96]"
                            >
                              Loading...
                            </td>
                          </tr>
                        ) : filterData.length > 0 ? (
                          filterData.map((item, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-gray-200 hover:bg-gray-100 text-center"
                            >
                              <td className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-white !border-r !border-[#F1935C]">
                                {item.time_slot ? item.time_slot : "-"}
                              </td>

                              <td
                                className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-blue-50 !border-r !border-[#F1935C] cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() =>
                                  handleColumnClick(
                                    "total_generation",
                                    item.time_slot
                                  )
                                }
                              >
                                <span className="underline decoration-dotted">
                                  {item.total_generation
                                    ? item.total_generation.toFixed(2)
                                    : "-"}
                                </span>
                              </td>

                              <td
                                className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-white !border-r !border-[#F1935C] cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() =>
                                  handleColumnClick(
                                    "total_must_run",
                                    item.time_slot
                                  )
                                }
                              >
                                <span className="underline decoration-dotted">
                                  {item.total_must_run
                                    ? item.total_must_run.toFixed(2)
                                    : "-"}
                                </span>
                              </td>

                              <td
                                className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-blue-50 !border-r !border-[#F1935C] cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() =>
                                  handleColumnClick(
                                    "total_available",
                                    item.time_slot
                                  )
                                }
                              >
                                <span className="underline decoration-dotted">
                                  {item.total_available
                                    ? item.total_available.toFixed(2)
                                    : "-"}
                                </span>
                              </td>

                              <td className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-white !border-r !border-[#F1935C]">
                                {item.demand_mw
                                  ? item.demand_mw.toFixed(2)
                                  : "-"}
                              </td>

                              <td className="py-3 px-4 text-center whitespace-normal max-w-[200px] bg-blue-50">
                                {item.surplus_deficit
                                  ? item.surplus_deficit.toFixed(2)
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className=" flex items-center justify-center overflow-auto capitalize text-nowrap rounded-lg w-full h-full">
                            <h1 className="chartHeading !text-base text-center !font-medium w-100">
                              No LGBR Data Available...
                            </h1>
                          </div>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
                <ErrorPopup error={error} onClose={() => setError(null)} />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
